import { SolDialogState } from '../abstract-dialog-component.directive';
import { SolDialog } from '../../../models/core/sol-dialog';
import { SolText } from '../../../models/core/sol-text';
import { SolType } from '../../../models/core/sol-type';
import { SolValidationMessage } from '../../../models/core/sol-validation-message';
import { SolValidatorDef } from '../../../models/core/sol-validator';
import { SolForm } from '../../../models/form/sol-form';
import { SolFormDef } from '../../../models/form/sol-form-def';
import { SolFormFieldDef } from '../../../models/form/sol-form-field-def';
import { ObjectTools } from '../../../tools/object-tools';

export interface SolSelectAddValueFormData {
  value: string;
}

export class SolSelectAddValueDialogState implements SolDialogState {
  private static readonly valueMaxLength = 255;
  private static readonly valueLabel = 'sol-select.add-value.field';

  private constructor(
    public readonly dialog: SolDialog,
    public readonly form: SolForm<SolSelectAddValueFormData>,
    public readonly value: string | undefined,
  ) {}

  public static of(): SolSelectAddValueDialogState {
    return new SolSelectAddValueDialogState(
      SolDialog.ofConfirm(SolText.of('sol-select.add-value.title'), SolText.of('sol-common.add')).withWidth('small'),
      SolSelectAddValueDialogState.buildForm(),
      undefined,
    );
  }

  public withDialog(dialog: SolDialog): this {
    return ObjectTools.copy<SolSelectAddValueDialogState>(this, { dialog }) as this;
  }

  public withForm(form: SolForm<SolSelectAddValueFormData>): SolSelectAddValueDialogState {
    if (this.form === form) {
      return this;
    }
    return this.copy({ form }).recalculateValue();
  }

  public withValidated(): SolSelectAddValueDialogState {
    return this.copy({ form: this.form.validateAll() }).recalculateValue();
  }

  public withErrors(messages: SolValidationMessage[]): SolSelectAddValueDialogState {
    return this.copy({ form: this.form.validateAll(messages) }).recalculateValue();
  }

  private recalculateValue(): SolSelectAddValueDialogState {
    const trimmed = this.form.data.value?.trim() ?? '';
    return this.copy({ value: this.form.valid && trimmed.length > 0 ? trimmed : undefined });
  }

  private copy(props: Partial<SolSelectAddValueDialogState>): SolSelectAddValueDialogState {
    return ObjectTools.copy<SolSelectAddValueDialogState>(this, props);
  }

  private static buildForm(): SolForm<SolSelectAddValueFormData> {
    const formDef = SolFormDef.of<SolSelectAddValueFormData>(
      SolType.ofObject<SolSelectAddValueFormData>('solSelectAddValueFormData', {
        value: SolType.string.extend({
          validators: [
            SolValidatorDef.required.of({ name: SolSelectAddValueDialogState.valueLabel }),
            SolValidatorDef.maxLength.of({
              name: SolSelectAddValueDialogState.valueLabel,
              length: SolSelectAddValueDialogState.valueMaxLength,
            }),
          ],
        }),
      }),
      // The dialog title already names the task, so the field carries its label only for validation copy.
      [SolFormFieldDef.of('value', SolSelectAddValueDialogState.valueLabel).withLabelVisible(false)],
    );
    return SolForm.of<SolSelectAddValueFormData>(formDef, { value: '' }, { isNew: true });
  }
}
